import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, animate, useMotionValue, useMotionValueEvent, useReducedMotion, useTransform } from "framer-motion";
import {
  Check as CheckIcon, Copy as CopyIcon, ExternalLink as ExternalLinkIcon, FileText as FileTextIcon,
  Package as PackageIcon, Plus as PlusIcon, Printer as PrinterIcon, RotateCcw as RotateCcwIcon, Truck as TruckIcon,
} from "lucide-react";
import { TicketEndpoint } from "./ShipmentTicket";
import { BTN_OUTLINE, BTN_PRIMARY, MODE_ICON, MODE_STYLE, SPRING, fmtInr, fmtStampDate, initialsOf, relativeDays, riseIn } from "./tokens";

/* ═══════════════════ Booked — one wide boarding-pass ticket ═══════════════════
   A single elegant object, in the app's own ticket language: a refined ink stamp pressed
   onto the top edge, the route drawn full-width with a marker-truck gliding origin→
   destination, then a perforated stub carrying the four facts that matter (courier,
   arrival, charge, AWB) and the order meta as a quiet line. Brand blue leads; emerald
   only where "booked" is stated. Every motion respects prefers-reduced-motion. */

/* Confetti that bursts from the stamp as it lands — brand palette, small pieces, clipped to
   the card so it can never widen the page. Skipped entirely under reduced motion. */
const CONFETTI_COLOURS = ["#3877FC", "#2A59BD", "#10B981", "#34D399", "#F59E0B", "#93C5FD"];
export function StampConfetti() {
  const parts = useRef(null);
  const rain = useRef(null);
  const layerRef = useRef(null);
  const [fallH, setFallH] = useState(720);
  const [phase, setPhase] = useState("live"); // live → fading → gone (after ~30s)
  if (!parts.current) {
    // Three opening bursts from the stamp.
    const WAVES = [0.28, 1.25, 2.3];
    parts.current = WAVES.flatMap((base, w) => Array.from({ length: 20 }).map((_, i) => ({
      dx: (Math.random() * 2 - 1) * (300 + w * 40),
      rise: 24 + Math.random() * 60,
      fall: 190 + Math.random() * 110,
      rot: (Math.random() * 2 - 1) * 440,
      delay: base + Math.random() * 0.22,
      dur: 1.6 + Math.random() * 0.6,
      w: 5 + Math.random() * 4,
      round: (i + w) % 4 === 0,
      color: CONFETTI_COLOURS[(i + w) % CONFETTI_COLOURS.length],
    })));
    // Then a slow, sparse confetti fall across the card, looping for half a minute.
    rain.current = Array.from({ length: 34 }).map((_, i) => ({
      left: Math.random() * 100,
      sway: (Math.random() * 2 - 1) * 46,
      rot: (Math.random() * 2 - 1) * 720,
      delay: 1.2 + Math.random() * 6,
      dur: 5.5 + Math.random() * 4,
      gap: Math.random() * 2.5,
      w: 5 + Math.random() * 4,
      round: i % 3 === 0,
      color: CONFETTI_COLOURS[i % CONFETTI_COLOURS.length],
    }));
  }
  useLayoutEffect(() => {
    const el = layerRef.current;
    if (!el) return undefined;
    const measure = () => setFallH(el.clientHeight);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    const fade = window.setTimeout(() => setPhase("fading"), 30000);
    const gone = window.setTimeout(() => setPhase("gone"), 31600);
    return () => { window.clearTimeout(fade); window.clearTimeout(gone); };
  }, []);
  if (phase === "gone") return null;
  return (
    <motion.div ref={layerRef} aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 overflow-hidden rounded-[28px]"
      animate={{ opacity: phase === "fading" ? 0 : 1 }} transition={{ duration: 1.4 }}>
      <div className="absolute left-1/2 top-3 h-0 w-0">
        {parts.current.map((p, i) => (
          <motion.span
            key={i}
            className={`absolute block ${p.round ? "rounded-full" : "rounded-[1.5px]"}`}
            style={{ width: p.w, height: p.round ? p.w : p.w * 0.6, background: p.color }}
            initial={{ x: 0, y: 0, opacity: 0, rotate: 0 }}
            animate={{ x: p.dx, y: [0, -p.rise, p.fall], opacity: [1, 1, 0], rotate: p.rot }}
            transition={{ duration: p.dur, delay: p.delay, ease: "easeOut" }}
          />
        ))}
      </div>
      {rain.current.map((p, i) => (
        <motion.span
          key={`r${i}`}
          className={`absolute top-0 block ${p.round ? "rounded-full" : "rounded-[1.5px]"}`}
          style={{ left: `${p.left}%`, width: p.w, height: p.round ? p.w : p.w * 0.6, background: p.color }}
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: [-24, fallH + 24], x: [0, p.sway, 0], rotate: [0, p.rot], opacity: [0, 1, 1, 0] }}
          transition={{ duration: p.dur, delay: p.delay, repeat: Infinity, repeatDelay: p.gap, ease: "linear" }}
        />
      ))}
    </motion.div>
  );
}

/* The route — the same treatment as the shipment-preview ticket: 2px base line, four leg
   ticks that turn blue as the truck passes, the rainbow trail behind it, blue endpoint dots,
   and the brand truck riding the line. Here the truck makes the whole journey slowly (8s);
   the destination stays a hollow dot, so nothing claims delivery. Reduced motion parks it. */
export function RouteDrive({ originCity, originPin, destCity, destPin, reduce, rtl = false }) {
  const p = useMotionValue(reduce ? 1 : 0);          // journey progress 0 → 1
  const [legs, setLegs] = useState(reduce ? 4 : 0);   // ticks passed, for colouring
  useMotionValueEvent(p, "change", (v) => setLegs(Math.min(4, Math.floor(v * 5))));
  useEffect(() => {
    if (reduce) { p.set(1); return undefined; }
    const controls = animate(p, 1, { duration: 8, ease: [0.45, 0, 0.25, 1], delay: 0.6 });
    return () => controls.stop();
  }, [p, reduce]);
  // distance along the line from the origin's edge — the left edge outbound, the right edge for a return
  const truckAlong = useTransform(p, (v) => `calc(5px + ${v} * (100% - 10px))`);
  const trailWidth = useTransform(p, (v) => `calc(${v} * (100% - 10px))`);
  // tick i (numbered left → right) is passed once the truck has crossed it, whichever way it drives
  const passed = (i) => (rtl ? legs >= 5 - i : legs >= i);
  const from = <TicketEndpoint label="From" data={{ pin: originPin, city: originCity }} size="lg" align={rtl ? "right" : "left"} />;
  const to = <TicketEndpoint label={rtl ? "To you" : "To"} data={{ pin: destPin, city: destCity }} size="lg" align={rtl ? "left" : "right"} />;
  return (
    <div className="mt-3 grid grid-cols-2 items-center gap-x-4 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto] sm:gap-x-6">
      {/* the preview ticket's endpoints, reused (lg): mono pincode leads, city as the caption.
          On sm+ they flank the line like a boarding-pass route strip; on mobile they sit above it.
          A return puts the customer on the right — the parcel is coming back to you. */}
      {rtl ? to : from}
      <div className="order-2 min-w-0 justify-self-end sm:order-3">
        {rtl ? from : to}
      </div>
      {/* the track is tall enough that the 60px truck's roof stays inside it; mx-2 keeps the
          truck clear of the endpoint blocks / stub edges at both ends */}
      <div className="relative order-3 col-span-2 mx-2 h-[80px] sm:order-2 sm:col-span-1">
        <div className="absolute left-[5px] right-[5px] top-1/2 h-[2px] -translate-y-1/2 rounded-full bg-slate-200" />
        {[1, 2, 3, 4].map((i) => (
          <span
            key={i}
            className={`absolute top-1/2 h-[7px] w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-full transition-colors duration-300 ${passed(i) ? "bg-[#3877FC]" : "bg-slate-300"}`}
            style={{ left: `calc(5px + ${i / 5} * (100% - 10px))` }}
            aria-hidden="true"
          />
        ))}
        <motion.div className={`cfo-rainbow absolute top-1/2 h-[2px] -translate-y-1/2 rounded-full ${rtl ? "right-[5px]" : "left-[5px]"}`} style={{ width: trailWidth }} aria-hidden="true" />
        <span className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full bg-[#3877FC] ${rtl ? "right-0" : "left-0"}`} />
        <span className={`absolute top-1/2 h-2.5 w-2.5 -translate-y-1/2 rounded-full border-2 border-[#3877FC] bg-white ${rtl ? "left-0" : "right-0"}`} />
        <motion.span className="absolute top-1/2 z-10" style={rtl ? { right: truckAlong, transform: "translate(50%, -50%)" } : { left: truckAlong, transform: "translate(-50%, -50%)" }} aria-hidden="true">
          {/* the preview's sprite, a third larger for the confirmation page; wheels still ride the line.
              A return drives the other way, so the sprite is mirrored. */}
          <span className={`wv-ticket-truck block h-[40px] w-[60px] -translate-y-[17px] [filter:drop-shadow(0_2px_3px_rgba(15,23,42,0.3))] ${rtl ? "-scale-x-100" : ""}`} />
          <span className="wv-ticket-truck-fallback h-9 w-9 place-items-center rounded-full border-2 border-[#3877FC] bg-white text-[#2A59BD] shadow-sm">
            <TruckIcon className={`h-4 w-4 ${rtl ? "-scale-x-100" : ""}`} />
          </span>
        </motion.span>
      </div>
    </div>
  );
}

/* Code 39 barcode of the AWB — a real, scannable encoding of the tracking number (A–Z, 0–9,
   "-", ".", space, $, /, +, %; lower-case is upper-cased, as scanners read it), so the ticket
   carries its AWB the way a boarding pass does. The table is built from the Code 39 structure
   (bar pattern by position × wide-space group per character set) rather than typed by hand. */
const CODE39 = (() => {
  const bars = ["10001", "01001", "11000", "00101", "10100", "01100", "00011", "10010", "01010", "00110"];
  const groups = [["1234567890", "0100"], ["ABCDEFGHIJ", "0010"], ["KLMNOPQRST", "0001"], ["UVWXYZ-. *", "1000"]];
  const weave = (b, s) => Array.from({ length: 9 }, (_, i) => ((i % 2 === 0 ? b[i / 2] : s[(i - 1) / 2]) === "1" ? "w" : "n")).join("");
  const table = {};
  groups.forEach(([chars, spaces]) => chars.split("").forEach((c, i) => { table[c] = weave(bars[i], spaces); }));
  Object.entries({ $: "1110", "/": "1101", "+": "1011", "%": "0111" }).forEach(([c, spaces]) => { table[c] = weave("00000", spaces); });
  return table;
})();
export function AwbBarcode({ value, className = "" }) {
  const text = String(value || "").trim().toUpperCase();
  if (text.length < 4 || [...text].some((c) => !CODE39[c])) return null;
  const rects = [];
  let x = 0;
  `*${text}*`.split("").forEach((c) => {
    const pattern = CODE39[c];
    for (let i = 0; i < 9; i++) {
      const width = pattern[i] === "w" ? 3 : 1;
      if (i % 2 === 0) rects.push([x, width]); // even indexes are bars, odd are spaces
      x += width;
    }
    x += 1; // inter-character gap
  });
  const total = x - 1;
  return (
    <svg className={className} viewBox={`0 0 ${total} 40`} preserveAspectRatio="none" shapeRendering="crispEdges" role="img" aria-label={`AWB ${text} barcode`}>
      {rects.map(([bx, bw], i) => <rect key={i} x={bx} y="0" width={bw} height="40" fill="#0F172A" />)}
    </svg>
  );
}

export function Fact({ label, children, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">{label}</div>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

export function Detail({ k, v, mono = false, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">{k}</div>
      <div className={`mt-1 text-[13.5px] font-semibold leading-snug text-slate-800 ${mono ? "break-all tabular-nums" : "break-words"}`}>{v}</div>
    </div>
  );
}

/* `variant="return"` makes the ticket unmistakably a reverse pickup: a RETURN chip on the stub,
   the route driven right → left with the customer on the right, and return wording throughout.
   `headline` / `partyLabel` can still override the copy. The default is the forward ticket, unchanged. */
export default function BookedSuccess({
  bookedAt, origin, destination, courier, charge, settlement, billedKg, billedBasis, order, labelUrl, invoiceUrl, awbCopied, onCopyAwb, onViewDetails, onAllShipments, onBookAnother,
  variant = "forward", headline, partyLabel,
}) {
  const rtl = variant === "return";
  const heading = headline || (rtl
    ? <>Return placed. <span className="whitespace-nowrap">Pickup is next.</span></>
    : <>Order placed. <span className="whitespace-nowrap">Pickup is next.</span></>);
  const party = partyLabel || (rtl ? "Customer" : "Recipient");
  const reduce = useReducedMotion();
  const titleCase = (s) => String(s || "").trim().toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
  const originCity = titleCase(origin?.city) || (rtl ? "the customer" : "Pickup");
  const destCity = titleCase(destination?.city) || (rtl ? "your location" : "Destination");
  const stampDate = bookedAt ? fmtStampDate(bookedAt) : null;
  const ModeIcon = courier?.mode ? MODE_ICON[courier.mode] : null;
  const accent = courier?.accent || "#3877FC";
  // Wallets are debited at booking (present tense); COD net-off settles later, at remittance (future).
  const settlementLine = settlement
    ? (settlement === "COD net-off" ? "Will be settled from your COD net-off" : `Settled from ${settlement}`)
    : null;

  return (
    <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={SPRING} className="mx-auto w-full max-w-[1180px] pb-5 pt-9 sm:pt-10">
      {/* ── The ticket ── */}
      <div className="relative">
        {/* Shadow lives on a halo layer, off the paper, so the notches stay true cut-outs */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-[28px] shadow-[0_30px_70px_-42px_rgba(15,23,42,0.4)]" />
        {!reduce && <StampConfetti />}

        {/* Ink stamp — pressed onto the top edge, centred */}
        <div className="absolute left-1/2 top-0 z-20 -translate-x-1/2 -translate-y-1/2">
          <motion.div
            initial={reduce ? false : { scale: 1.45, opacity: 0, rotate: -13 }}
            // slam down, press into the paper, rock twice as it seats, settle — a stamp impression
            animate={reduce ? { scale: 1, opacity: 1, rotate: -5 } : { scale: [1.45, 0.96, 1.03, 0.99, 1], opacity: [0, 1, 1, 1, 1], rotate: [-13, -3.5, -7.5, -4.2, -5] }}
            transition={reduce ? { duration: 0 } : { duration: 1.15, times: [0, 0.28, 0.5, 0.72, 1], ease: "easeOut", delay: 0.12 }}
            className="relative"
          >
            {!reduce && (
              <motion.span aria-hidden="true" className="absolute inset-0 rounded-[14px] border border-emerald-500/50"
                initial={{ scale: 0.85, opacity: 0.5 }} animate={{ scale: 1.5, opacity: 0 }} transition={{ duration: 0.7, delay: 0.18, ease: "easeOut" }} />
            )}
            <div className="rounded-[14px] border-2 border-emerald-600/80 bg-white p-[3px] shadow-[0_8px_20px_-12px_rgba(16,185,129,0.5)]">
              <div className="rounded-[10px] border border-emerald-600/60 px-6 py-2 text-center">
                <div className="text-[22px] font-black uppercase leading-none tracking-[0.24em] text-emerald-700">Booked</div>
                {stampDate && <div className="mt-1 whitespace-nowrap text-[10px] font-bold uppercase tracking-[0.08em] tabular-nums text-emerald-700/75">{stampDate}</div>}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Upper stub: headline + route, on a softly tinted, lightly mapped ground */}
        <div className="relative overflow-hidden rounded-t-[28px] rounded-b-[12px] border border-b-0 border-[#E3E9F2] bg-white px-6 pb-4 pt-10 sm:px-10 sm:pt-11">
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-40" style={{ background: "linear-gradient(to bottom, rgba(56,119,252,0.075), rgba(56,119,252,0.025) 50%, transparent)" }} />
          <div aria-hidden="true" className="pointer-events-none absolute left-1/2 top-0 h-32 w-[420px] max-w-full -translate-x-1/2 -translate-y-1/2 rounded-full" style={{ background: "radial-gradient(closest-side, rgba(16,185,129,0.13), transparent)" }} />
          <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-36" style={{ backgroundImage: "radial-gradient(rgba(30,58,109,0.09) 1px, transparent 1.4px)", backgroundSize: "18px 18px", maskImage: "linear-gradient(to bottom, transparent, black 35%, black 70%, transparent)", WebkitMaskImage: "linear-gradient(to bottom, transparent, black 35%, black 70%, transparent)" }} />
          {/* Headline */}
          <motion.div {...riseIn(reduce, 0.3)} className="text-center">
            {/* A return wears its label on the stub, where the eye lands first: the top-left corner on
                wide screens; on phones (where that corner is the stamp's) it sits above the headline. */}
            {rtl && (
              <span className="mb-2.5 inline-flex items-center gap-1.5 rounded-full border border-[#3877FC]/30 bg-white/90 px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-[#2A59BD] shadow-[0_1px_2px_rgba(15,23,42,0.06)] backdrop-blur sm:absolute sm:left-8 sm:top-7 sm:z-10 sm:mb-0">
                <RotateCcwIcon className="h-3 w-3" strokeWidth={2.75} /> Return · reverse pickup
              </span>
            )}
            <h2 className="text-[22px] font-extrabold tracking-tight text-slate-900 sm:text-[27px]">{heading}</h2>
            <p className="mt-1.5 text-[15px] text-slate-500">
              {courier?.carrier
                ? <><b className="font-semibold text-slate-700">{courier.carrier}</b> will collect it from <b className="font-semibold text-slate-700">{originCity}</b>{courier?.edd && courier?.tat != null ? (rtl
                    ? <> · back at <b className="font-semibold text-slate-700">{destCity}</b> expected <b className="whitespace-nowrap font-semibold text-slate-700">{relativeDays(courier.tat)}</b> <span className="whitespace-nowrap text-slate-400">({courier.edd})</span></>
                    : <> · delivery expected <b className="whitespace-nowrap font-semibold text-slate-700">{relativeDays(courier.tat)}</b> <span className="whitespace-nowrap text-slate-400">({courier.edd})</span></>) : null}</>
                : <>Your courier will collect it from <b className="font-semibold text-slate-700">{originCity}</b></>}
            </p>
          </motion.div>

          {/* Route */}
          <RouteDrive originCity={originCity} originPin={origin?.pin} destCity={destCity} destPin={destination?.pin} reduce={reduce} rtl={rtl} />

        </div>

        {/* Perforation: the two stubs meet here — their 12px seam corners form the notches as
            real cut-outs, so the page's own background shows through them. */}
        <div className="relative z-10 h-0" aria-hidden="true">
          <div className="absolute inset-x-[14px] top-0 -translate-y-1/2 border-t-2 border-dotted border-slate-300" />
        </div>

        {/* Lower stub: the facts + order meta */}
        <div className="relative rounded-b-[28px] rounded-t-[12px] border border-t-0 border-[#E3E9F2] bg-white px-6 pb-5 pt-4 sm:px-10">

          {/* The AWB — the king of this ticket: big, copyable, and a real Code 39 barcode */}
          <motion.div {...riseIn(reduce, 0.4)} className="flex flex-wrap items-center justify-between gap-x-10 gap-y-4">
            <div className="min-w-0">
              <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-slate-400">AWB / Tracking number</div>
              {order?.waybill ? (
                <button type="button" onClick={onCopyAwb} className="group mt-1.5 inline-flex max-w-full items-center gap-2.5 text-left">
                  {/* never truncate the AWB — wrap a rare over-long one instead of hiding characters */}
                  <span className="break-all text-[22px] font-black leading-tight tracking-[0.04em] tabular-nums text-slate-900 transition-colors group-hover:text-[#2A59BD] sm:text-[30px] sm:leading-none">{order.waybill}</span>
                  {awbCopied ? <CheckIcon className="h-5 w-5 flex-shrink-0 text-emerald-600" strokeWidth={3} /> : <CopyIcon className="h-5 w-5 flex-shrink-0 text-slate-400 transition-colors group-hover:text-[#3877FC]" />}
                </button>
              ) : (
                <div className="mt-1.5 text-[17px] font-semibold text-slate-600">Assigned shortly</div>
              )}
            </div>
            {order?.waybill && <AwbBarcode value={order.waybill} className="h-12 w-full max-w-[460px] flex-shrink-0 sm:w-[460px]" />}
          </motion.div>
          <div className="my-3 border-t border-slate-100" aria-hidden="true" />

          {/* Three facts */}
          <motion.div {...riseIn(reduce, 0.45)} className="grid gap-x-8 gap-y-5 sm:grid-cols-3">
            <Fact label="Courier">
              <div className="flex items-center gap-2.5">
                <span className="grid h-11 w-11 flex-shrink-0 place-items-center rounded-xl text-[14px] font-extrabold"
                  style={{ backgroundImage: `radial-gradient(circle at 30% 22%, ${accent}33, transparent 62%), linear-gradient(150deg, ${accent}1F, ${accent}0A)`, color: accent, boxShadow: `inset 0 0 0 1px ${accent}2E` }}>
                  {initialsOf(courier?.carrier || "?")}
                </span>
                <div className="min-w-0">
                  <div className="truncate text-[16px] font-bold leading-tight text-slate-900">{courier?.carrier || "Allocating"}</div>
                  {courier?.mode && (
                    <span className={`mt-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-[1px] text-[9.5px] font-bold uppercase tracking-wide ring-1 ring-inset ${MODE_STYLE[courier.mode] || "bg-slate-100 text-slate-500 ring-slate-200/70"}`}>
                      {ModeIcon && <ModeIcon className="h-2.5 w-2.5" strokeWidth={2.25} />}{courier.mode}
                    </span>
                  )}
                </div>
              </div>
            </Fact>

            <Fact label={rtl ? "Expected back" : "Expected delivery"}>
              {courier?.edd
                ? <><div className="text-[20px] font-bold leading-tight text-slate-900">{courier.edd}</div>{courier?.tat != null && <div className="mt-0.5 text-[13px] text-slate-400">up to {courier.tat} day{courier.tat === 1 ? "" : "s"} from pickup</div>}</>
                : <div className="text-[15px] font-semibold text-slate-600">Shared once the carrier scans it</div>}
            </Fact>

            <Fact label="Shipment charge">
              {charge != null
                ? <><div className="flex items-baseline gap-1.5"><span className="text-[26px] font-black leading-none tabular-nums text-slate-900">{fmtInr(charge)}</span><span className="text-[12px] font-medium text-slate-400">incl. GST</span></div>{settlementLine && <div className="mt-1.5 text-[13px] text-slate-400">{settlementLine}</div>}</>
                : <div className="text-[15px] font-semibold text-slate-600">Confirmed at billing</div>}
            </Fact>

          </motion.div>

          {/* Order details — a calm labelled grid; long ids get their own cell */}
          <motion.div {...riseIn(reduce, 0.55)} className="mt-3 grid gap-x-6 gap-y-4 border-t border-slate-100 pt-3 sm:grid-cols-2 lg:grid-cols-8">
            <Detail k={party} v={[order?.recipientName, order?.recipientPhone].filter(Boolean).join(" · ") || "—"} className="lg:col-span-2" />
            <Detail k={order?.paymentLabel || "Payment"} v={order?.paymentValue || "—"} />
            <Detail k={order?.itemsCount === 1 ? "Item" : "Items"} v={order?.itemsCount ? String(order.itemsCount) : "—"} />
            <Detail k="Chargeable wt" v={billedKg != null ? `${billedKg.toFixed(2)} kg${billedBasis ? ` (${billedBasis === "vol" ? "volumetric" : "dead"})` : ""}` : "—"} />
            <Detail k="Order reference" v={order?.orderRef || "—"} mono className="sm:col-span-2 lg:col-span-3" />
          </motion.div>

          {/* ── Card footer: the actions live inside the ticket, flush with its bottom — never in its shadow ── */}
          {/* Phones: one full-width primary, then the secondaries two-up (a wrapped cluster reads
              as a jumble at 390px). Desktop: the same buttons in a centred row. */}
          <motion.div {...riseIn(reduce, 0.65)} className="-mx-6 -mb-5 mt-4 grid gap-2.5 rounded-b-[28px] border-t border-slate-100 bg-[#FAFBFD] px-6 py-4 sm:-mx-10 sm:flex sm:flex-wrap sm:items-center sm:justify-center sm:px-10">
            <button type="button" onClick={onViewDetails} className={`${BTN_PRIMARY} cfo-cta-ready h-12 w-full justify-center sm:h-11 sm:w-auto`}>
              <ExternalLinkIcon className="h-4 w-4" /> View order details
            </button>
            <div className="grid grid-cols-2 gap-2.5 sm:contents">
              {labelUrl && <a href={labelUrl} target="_blank" rel="noopener noreferrer" className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PrinterIcon className="h-4 w-4" /> Print label</a>}
              {invoiceUrl && <a href={invoiceUrl} target="_blank" rel="noopener noreferrer" className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><FileTextIcon className="h-4 w-4" /> Invoice</a>}
              <button type="button" onClick={onAllShipments} className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PackageIcon className="h-4 w-4" /> All shipments</button>
              <button type="button" onClick={onBookAnother} className={`${BTN_OUTLINE} h-12 justify-center sm:h-11`}><PlusIcon className="h-4 w-4" /> Book another</button>
            </div>
          </motion.div>
        </div>
      </div>

    </motion.div>
  );
}
